import { useEffect } from "react"
import { toast } from "@/components/ui/toast"
import { fetchPublishedVersion } from "@/lib/app-update"

const checkIntervalMs = 10 * 60_000
const minCheckGapMs = 60_000
const toastId = "app-update"

export function useAppUpdateNotice(currentVersion: string, enabled = import.meta.env.PROD) {
  useEffect(() => {
    if (!enabled) return

    let lastCheck = 0
    let dismissedVersion: string | null = null
    let cancelled = false

    async function check(force = false) {
      const now = Date.now()
      if (!force && now - lastCheck < minCheckGapMs) return
      lastCheck = now

      const publishedVersion = await fetchPublishedVersion()
      if (cancelled || !publishedVersion || publishedVersion === currentVersion) return
      if (publishedVersion === dismissedVersion) return

      // The fixed id updates the open toast instead of stacking another one.
      toast.add({
        id: toastId,
        title: "Nova versão do Farol disponível",
        description: "Atualize a página para usar a versão mais recente.",
        timeout: 0,
        actionProps: { children: "Atualizar", onClick: () => window.location.reload() },
        onClose: () => { dismissedVersion = publishedVersion },
      })
    }

    function checkWhenVisible() {
      if (document.visibilityState === "visible") void check()
    }

    const interval = window.setInterval(checkWhenVisible, checkIntervalMs)
    document.addEventListener("visibilitychange", checkWhenVisible)
    window.addEventListener("focus", checkWhenVisible)
    void check(true)

    return () => {
      cancelled = true
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", checkWhenVisible)
      window.removeEventListener("focus", checkWhenVisible)
    }
  }, [currentVersion, enabled])
}
